import { computeKinematics } from "../forwardKinematics";
import type { IkChain, IkKinematicsState, IkLink, IkResult } from "../types";
import { getSceneActions } from "../../stores/sceneStore";
import { clamp } from "./spatialUtils";

type ApplyPickPlaceJointCommandArgs = {
  attempt: IkResult;
  chain: IkChain;
  dofLinks: IkLink[];
  currentRefs: number[];
  lastSolution: number[] | null;
  dt: number;
};

export function applyPickPlaceJointCommand({
  attempt,
  chain,
  dofLinks,
  currentRefs,
  lastSolution,
  dt,
}: ApplyPickPlaceJointCommandArgs): {
  lastSolution: number[];
  fkAfter: IkKinematicsState | null;
} {
  const commandRefs =
    lastSolution && lastSolution.length === chain.dofCount ? lastSolution : currentRefs;
  const maxVel = 1.2;
  const maxDelta = maxVel * dt;
  const blended = attempt.jointValues.map((targetVal, idx) => {
    const cur = commandRefs[idx] ?? currentRefs[idx] ?? 0;
    const diff = targetVal - cur;
    const step = clamp(diff, -maxDelta, maxDelta);
    return cur + step;
  });

  const minStep = 5e-4;
  const jointUpdates: Array<{ jointUuid: string; value: number }> = [];
  blended.forEach((val, idx) => {
    const link = dofLinks.find((l) => l.dofIndex === idx) || null;
    const jointUuid = link?.jointUuid;
    if (jointUuid && Math.abs(val - (commandRefs[idx] ?? currentRefs[idx] ?? 0)) >= minStep) {
      jointUpdates.push({ jointUuid, value: val });
    }
  });
  if (jointUpdates.length > 0) {
    getSceneActions().updateJointValues(jointUpdates);
  }

  const fkAfter: IkKinematicsState | null = (() => {
    try {
      return computeKinematics(chain, blended);
    } catch {
      return null;
    }
  })();

  return { lastSolution: blended, fkAfter };
}
